"use client";

import {
  MembersHeader,
  sideMenuToggleButtonId,
} from "@/app/members/(use-shared-layout)/MembersHeader";
import {
  MembersSideMenu,
  sideMenuId,
} from "@/app/members/(use-shared-layout)/MembersSideMenu";
import { Overlay } from "@/components/ui/Overlay";
import { cn } from "@/lib/utils";
import type { MemberEligibility } from "@/server/models/member/member-eligibility";
import { ReactNode, useEffect, useState } from "react";

export const MembersSharedLayout = ({
  relatedOffice,
  insuredNumber,
  branchNumber,
  memberName,
  memberEligibility,
  actionRequiredApplicationsCount,
  children,
}: {
  relatedOffice: { name: string; officeCode: string };
  insuredNumber: string;
  branchNumber: string;
  memberName: string;
  memberEligibility: MemberEligibility;
  actionRequiredApplicationsCount: number;
  children: ReactNode;
}) => {
  const [sideMenuOpened, setSideMenuOpened] = useState(false);
  const toggleSideMenuOpened = () => setSideMenuOpened((p) => !p);
  const closeSideMenu = () => setSideMenuOpened(false);

  useEffect(() => {
    // 遷移時などにサイドメニューが閉じないので下記の条件の要素をクリックした場合にはサイドメニューを閉じる
    const closeSideMenuOnClickOutside = (e: MouseEvent) => {
      const clickedEl = e.target as HTMLElement | null;
      if (
        // aタグは常に閉じる
        clickedEl?.closest("a") ||
        // サイドメニューのトグルボタンは要素自身でハンドリングするので不要
        (!clickedEl?.closest(`#${sideMenuToggleButtonId}`) &&
          // サイドメニューの中身をクリックした場合は閉じない、サイドメニュー内のリンクの場合は先にaタグの条件に引っかかるので閉じる
          !clickedEl?.closest(`#${sideMenuId}`))
      ) {
        // Safariでは新規タブに切り替えた後にサイドメニューが開かなくなるので、targetがある場合は閉じない
        if (clickedEl?.closest("a")?.target) {
          return;
        }
        closeSideMenu();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // タブがアクティブになったときにサイドメニューを閉じる
        closeSideMenu();
      }
    };

    document.addEventListener("click", closeSideMenuOnClickOutside);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("click", closeSideMenuOnClickOutside);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <div
      className={cn(
        "flex min-h-svh flex-row",
        // サイドメニューが開いているときはスクロールをブロック
        sideMenuOpened && "max-h-svh overflow-x-hidden",
      )}
    >
      <MembersSideMenu
        relatedOffice={relatedOffice}
        insuredNumber={insuredNumber}
        branchNumber={branchNumber}
        memberEligibility={memberEligibility}
        sideMenuOpened={sideMenuOpened}
        actionRequiredApplicationsCount={actionRequiredApplicationsCount}
      />
      <div className="w-full">
        <MembersHeader
          relatedOfficeName={relatedOffice.name}
          memberName={memberName}
          sideMenuOpened={sideMenuOpened}
          toggleSideMenuOpened={() => toggleSideMenuOpened()}
        />
        <Overlay
          className={cn(
            "z-20 hidden opacity-0 transition-opacity",
            sideMenuOpened && "block opacity-30",
          )}
          onClick={() => closeSideMenu()}
        />
        {children}
      </div>
    </div>
  );
};
